import ItemsPerPage from "components/Reusable/ItemsPerPage/index.tsx";
import { useContent } from "contexts/contentContext/contentContext.js";
import useInbox from "hooks/useInbox.ts";
import { usePicker } from "contexts/pickerContext/pickerContext.ts";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import InboxSortingComponent from "../Content/Toolbar/InboxSortingComponent.js";
import { useAppSelector } from "store/hooks.ts";
import {
  selectPickerSearchParams,
  selectTParam,
  selectFParam,
  selectCurrentPage,
  selectItemsPerPage,
} from "store/selectors/inboxSelectors.ts";

const FilesCounterComponent = () => {
  const { getCurrentPage, getIsFetching, getMailsQuantity } = useInbox();
  const pageType = useAppSelector(selectTParam);
  const searchParams = useAppSelector(selectPickerSearchParams);
  const {
    getProjectsQuantity,
    getIsFetchingPickerData,
    getCurrentPage: getPickerCurrentPage,
  } = usePicker();

  const getActiveProjectsQuantity = () => {
    return getProjectsQuantity()?.[
      searchParams?.p === "active" ? "totalActive" : "totalArchive"
    ];
  };

  if (pageType === "picker") {
    return (
      <FilesCounterRenderElement
        getMailsQuantity={getActiveProjectsQuantity}
        getCurrentPage={getPickerCurrentPage}
        getIsFetching={getIsFetchingPickerData}
      />
    );
  }

  const getCurrentCounter = () => getMailsQuantity();

  return (
    <FilesCounterRenderElement
      getMailsQuantity={getCurrentCounter}
      getCurrentPage={getCurrentPage}
      getIsFetching={getIsFetching}
    />
  );
};

const FilesCounterRenderElement = ({
  getMailsQuantity,
  getCurrentPage,
  getIsFetching,
}) => {
  const { getTextByKey } = useContent();
  const itemsPerPage = useAppSelector(selectItemsPerPage);

  return (
    <div className="files-counter">
      {!getMailsQuantity() ||
      getMailsQuantity() === 0 ||
      (getIsFetching() && getCurrentPage() === 1) ? null : (
        <>
          {getCurrentPage() === 1
            ? "1-" +
              (getMailsQuantity() < itemsPerPage
                ? getMailsQuantity()
                : itemsPerPage) +
              " "
            : (getCurrentPage() - 1) * itemsPerPage +
              1 +
              "-" +
              (getMailsQuantity() > getCurrentPage() * itemsPerPage
                ? getCurrentPage() * itemsPerPage
                : getMailsQuantity()) +
              " "}
          {getTextByKey("of-text")} {getMailsQuantity()}
        </>
      )}
    </div>
  );
};

const PaginationComponent = ({ isBottom }) => {
  const { getMailsQuantity, getIsFetching } = useInbox();
  const { getProjectsQuantity, getIsFetchingPickerData } = usePicker();
  const pickerSearchParams = useAppSelector(selectPickerSearchParams);
  const pageType = useAppSelector(selectTParam);

  if (pageType === "picker") {
    const filterType = pickerSearchParams?.p; // 'active'/'archived'/undefined

    const getProjectCount = () => {
      switch (filterType) {
        case "active":
          return +getProjectsQuantity()?.totalActive;
        case "archived":
          return +getProjectsQuantity()?.totalArchive;
        default:
          return (
            +getProjectsQuantity()?.totalActive +
            +getProjectsQuantity()?.totalArchive
          );
      }
    };

    return (
      <PaginationComponentRenderElement
        isBottom={isBottom}
        getMailsQuantity={getProjectCount}
        getIsFetching={getIsFetchingPickerData}
      />
    );
  }

  return (
    <PaginationComponentRenderElement
      isBottom={isBottom}
      getMailsQuantity={getMailsQuantity}
      getIsFetching={getIsFetching}
    />
  );
};

const PaginationComponentRenderElement = ({
  isBottom,
  getMailsQuantity,
  getIsFetching,
}) => {
  const { setPageNumber } = useInbox();
  const { getDirection } = useContent();
  const pageCategory = useAppSelector(selectFParam);
  const currentPage = useAppSelector(selectCurrentPage);
  const itemsPerPage = useAppSelector(selectItemsPerPage);

  if (
    !getMailsQuantity() ||
    getMailsQuantity() < 2 ||
    getIsFetching() ||
    (isBottom && getMailsQuantity() < itemsPerPage + 1)
  ) {
    return null;
  }

  const maxPages = Math.ceil(getMailsQuantity() / itemsPerPage);

  const handleFirstPage = () => {
    setPageNumber(1);
  };

  const handleLastPage = () => {
    setPageNumber(maxPages);
  };

  const handleSelectPage = (number) => {
    if (number === "...") return;
    setPageNumber(+number);
  };

  const getPaginationRange = () => {
    const range = [];
    const startPage = Math.max(currentPage - 2, 1);
    const endPage = Math.min(currentPage + 2, maxPages);

    // Add ellipsis before the range if startPage > 1
    if (startPage > 1) {
      range.push("...");
    }

    // Add the range of pages
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add ellipsis after the range if endPage < totalPages
    if (endPage < maxPages) {
      range.push("...");
    }

    return range;
  };

  return (
    <div
      className={`sorting-pagination-container${
        pageCategory === "jform" ? " moved-forms-filtering" : ""
      }${isBottom ? " external" : ""}`}
    >
      {isBottom ? null : <ItemsPerPage />}
      {isBottom ? null : <InboxSortingComponent />}
      <FilesCounterComponent />
      {getMailsQuantity() > itemsPerPage ? (
        <div className="pagination-elements">
          <Tooltip
            id={`showPrevInboxPage${isBottom ? "-bottom" : ""}`}
            tooltipTextKey={`tooltip-inbox-show-first-page`}
          >
            <div
              className={`arrow-container ${
                getDirection() === "ltr" ? "first-page" : ""
              }`}
              onClick={() => {
                handleFirstPage();
              }}
            >
              <Icon name="double-arrows" gray />
            </div>
          </Tooltip>
          <div className="pages-container">
            {getPaginationRange().map((pageNumber, index) => {
              return (
                <button
                  key={index}
                  className={`page-number${
                    +pageNumber === +currentPage ? " active" : ""
                  }${pageNumber === "..." ? " ellipsis" : ""}`}
                  onClick={() => {
                    handleSelectPage(pageNumber);
                  }}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>
          <Tooltip
            id={`showNextInboxPage${isBottom ? "-bottom" : ""}`}
            tooltipTextKey={`tooltip-inbox-show-last-page`}
          >
            <div
              className={`arrow-container ${
                getDirection() !== "ltr" ? "first-page" : ""
              }`}
              onClick={() => handleLastPage()}
            >
              <Icon name="double-arrows" gray />
            </div>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

export default PaginationComponent;
